import { useEffect, useMemo, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { Plus, ArrowRight, Clock, Wand2 } from '@/lib/icons';
import { useActiveEntities } from '@/stores/selectors';
import { useCodexStore } from '@/stores/codexStore';
import { matchEntity } from '@/lib/search/match';
import { ENTITY_META, entityColor } from '@/config/entityMeta';
import { MODULES } from '@/config/modules';
import type { Entity, EntityType } from '@/types/entity';

const TYPE_ROUTE: Record<EntityType, string> = {
  character: '/characters',
  planet: '/worlds',
  location: '/worlds',
  faction: '/codex',
  concept: '/concepts',
  storyline: '/plot',
  beat: '/plot',
  theme: '/',
  question: '/questions',
  note: '/codex',
};

const CREATE_TYPES: EntityType[] = ['character', 'planet', 'concept', 'question', 'note'];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  const all = useActiveEntities();
  const upsert = useCodexStore((s) => s.upsertEntity);

  // ⌘K / Ctrl+K + TopBar 이벤트
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onEvt = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('codex:palette', onEvt as EventListener);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('codex:palette', onEvt as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!open) setQ('');
  }, [open]);

  const entityResults = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const list = all.filter((e) => matchEntity(e, q));
    const score = (e: Entity) => {
      if (!ql) return 0;
      const n = e.name.toLowerCase();
      return n === ql ? 0 : n.startsWith(ql) ? 1 : n.includes(ql) ? 2 : 3;
    };
    return list.sort((a, b) => score(a) - score(b) || b.updatedAt - a.updatedAt).slice(0, 8);
  }, [all, q]);

  const recent = useMemo(
    () => [...all].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, 5),
    [all],
  );

  const navMatches = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return MODULES.filter((m) => !ql || m.label.toLowerCase().includes(ql) || m.path.includes(ql));
  }, [q]);

  const goEntity = (e: Entity) => {
    setOpen(false);
    navigate(`${TYPE_ROUTE[e.type]}?focus=${e.id}`);
  };
  const create = (type: EntityType) => {
    const name = q.trim() || `새 ${ENTITY_META[type].label}`;
    const id = upsert({ type, name });
    setOpen(false);
    navigate(`${TYPE_ROUTE[type]}?focus=${id}`);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-palette bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-[14vh] z-palette w-[92vw] max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border border-line-2 bg-popover shadow-pop data-[state=open]:animate-in data-[state=open]:fade-in"
        >
          <DialogPrimitive.Title className="sr-only">명령 팔레트</DialogPrimitive.Title>
          <Command shouldFilter={false} loop>
            <Command.Input
              value={q}
              onValueChange={setQ}
              placeholder="검색 · 이동 · 생성…"
              className="w-full border-b border-line bg-transparent px-4 py-3.5 text-sm text-ink outline-none placeholder:text-fainter"
            />
            <Command.List className="max-h-[58vh] overflow-y-auto p-2">
              <Command.Empty className="py-8 text-center text-sm text-faint">
                결과가 없다 — 다른 말로 찾거나 새로 만들어보자.
              </Command.Empty>

              {entityResults.length > 0 && (
                <Command.Group heading="엔티티" className="palette-group">
                  {entityResults.map((e) => {
                    const Icon = ENTITY_META[e.type].icon;
                    return (
                      <Command.Item
                        key={e.id}
                        value={`ent-${e.id}`}
                        onSelect={() => goEntity(e)}
                        className="palette-item"
                      >
                        <Icon className="size-4 flex-none" style={{ color: entityColor(e) }} />
                        <span className="truncate text-ink">{e.name}</span>
                        <span className="ml-auto font-mono text-[10px] uppercase text-faint">
                          {ENTITY_META[e.type].label}
                        </span>
                      </Command.Item>
                    );
                  })}
                </Command.Group>
              )}

              <Command.Group heading="생성" className="palette-group">
                {CREATE_TYPES.map((t) => {
                  const Icon = ENTITY_META[t].icon;
                  return (
                    <Command.Item
                      key={t}
                      value={`new-${t}`}
                      onSelect={() => create(t)}
                      className="palette-item"
                    >
                      <Plus className="size-4 flex-none text-star" />
                      <span className="text-dim">
                        {q.trim() ? (
                          <>
                            <span className="text-ink">“{q.trim()}”</span> {ENTITY_META[t].label}(으)로 생성
                          </>
                        ) : (
                          <>새 {ENTITY_META[t].label}</>
                        )}
                      </span>
                      <Icon className="ml-auto size-3.5 text-faint" />
                    </Command.Item>
                  );
                })}
              </Command.Group>

              <Command.Group heading="AI" className="palette-group">
                <Command.Item
                  value="ai-console"
                  onSelect={() => {
                    setOpen(false);
                    navigate('/');
                  }}
                  className="palette-item"
                >
                  <Wand2 className="size-4 flex-none text-star" />
                  <span className="text-dim">커널 — 에이전트에게 지시</span>
                </Command.Item>
              </Command.Group>

              {navMatches.length > 0 && (
                <Command.Group heading="이동" className="palette-group">
                  {navMatches.map((m) => {
                    const Icon = m.icon;
                    return (
                      <Command.Item
                        key={m.path}
                        value={`go-${m.path}`}
                        onSelect={() => {
                          setOpen(false);
                          navigate(m.path);
                        }}
                        className="palette-item"
                      >
                        <Icon className="size-4 flex-none text-faint" />
                        <span className="text-dim">{m.label}</span>
                        <ArrowRight className="ml-auto size-3.5 text-fainter" />
                      </Command.Item>
                    );
                  })}
                </Command.Group>
              )}

              {!q.trim() && recent.length > 0 && (
                <Command.Group heading="최근 편집" className="palette-group">
                  {recent.map((e) => {
                    const Icon = ENTITY_META[e.type].icon;
                    return (
                      <Command.Item
                        key={e.id}
                        value={`recent-${e.id}`}
                        onSelect={() => goEntity(e)}
                        className="palette-item"
                      >
                        <Clock className="size-3.5 flex-none text-fainter" />
                        <Icon className="size-3.5 flex-none" style={{ color: entityColor(e) }} />
                        <span className="truncate text-dim">{e.name}</span>
                      </Command.Item>
                    );
                  })}
                </Command.Group>
              )}
            </Command.List>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
